import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import * as _ from 'lodash';

import { CampaignContext } from 'src/contexts';
import { transformPartialFormToCampaign } from 'src/components/campaign/utils/transform';
import {
	getCampaignDelta,
	validateInitialPayload,
} from 'src/components/campaign/utils/delta';
import { ICampaign, ICampaignForm } from 'src/lib/schemas';
import { createOrUpdateCampaign } from 'src/services/campaign';
import { toastError } from 'src/services/toast';

const CAMPAIGN_FIELDS_OMIT = [
	'account',
	'status',
	'brief',
	'creatives',
	'schedule',
	'lastUpdatedBy',
	'updatedAt',
	'createdAt',
];

const useAutoSaveCampaign = () => {
	const {
		campaign,
		id: campaignId,
		setCampaign,
		isLaunching,
	} = useContext(CampaignContext);
	const [changedFields, setChangedFields] = useState<Partial<ICampaign>>({});
	const [isSaving, setIsSaving] = useState(false);
	const timeoutId = useRef<any>(null);
	const navigate = useNavigate();

	const cleanedCampaign = useMemo(() => {
		return _.omit(campaign, CAMPAIGN_FIELDS_OMIT);
	}, [campaign]);

	useEffect(() => {
		timeoutId.current = setTimeout(handleUpdateCampaign, 400);
		return () => clearTimeout(timeoutId.current);
	}, [changedFields]);

	const waitForSaving = () => {
		clearTimeout(timeoutId.current);
		timeoutId.current = setTimeout(handleUpdateCampaign, 400);
	};

	const isAllFieldsUndefined = (obj: any) => {
		return Object.values(obj).every((value) => value === undefined);
	};

	const handleUpdateCampaign = async () => {
		if (!campaignId || Object.keys(changedFields).length === 0) {
			setIsSaving(false);
			return;
		}
		if (isSaving) {
			waitForSaving();
			return;
		}
		setIsSaving(true);

		try {
			const delta = getCampaignDelta(cleanedCampaign, changedFields);
			const payload = _.omit(delta, ['id', 'status']);
			const { tone, goal, audience, promotion } = payload;
			// clean undefined fields
			if (goal === undefined) delete payload.goal;
			if (tone === undefined) delete payload.tone;
			if (audience === undefined) delete payload.audience;
			if (promotion === undefined) delete payload.promotion;

			const isEmptyPayload =
				_.isEmpty(payload) || isAllFieldsUndefined(payload);
			const isInvalidPayload = validateInitialPayload({
				...changedFields,
				group: undefined,
			});
			if (isEmptyPayload || isInvalidPayload) {
				setIsSaving(false);
				return;
			}

		// For campaigns that were already created, use the real ID for PATCH requests
		const effectiveCampaignId = campaignId === 'new' && campaign?.id
			? campaign.id
			: campaignId;

		// REMOVED: setCampaign({ ...campaign, ...payload }) - this was causing unnecessary re-renders
		// Only update with the API response to prevent duplicate state updates
		const response = await createOrUpdateCampaign(payload, effectiveCampaignId);
		setCampaign(response);
		if (effectiveCampaignId === 'new') {
			console.log('🎯 useAutoSaveCampaign (v1): Campaign created, updating URL', {
				newId: response.id,
				timestamp: new Date().toISOString(),
			});
			window.history.replaceState(
				{},
				'',
				`/projects/campaigns/${response.id}?status=draft`,
			);
		}
		} catch (error: any) {
			const errorMessage = error.response?.data?.message;
			const isTarget = typeof errorMessage === 'string' && errorMessage.includes('target');
			!isTarget && toastError(error);
		}
		setIsSaving(false);
	};

	const handleFieldsChange = (formData: Partial<ICampaignForm>) => {
		if (isLaunching) return;
		const transformedFormValues = transformPartialFormToCampaign(
			formData,
			campaign,
		);

		setChangedFields(transformedFormValues);
	};

	return { onFieldsChange: handleFieldsChange };
};

export default useAutoSaveCampaign;
